import React from 'react';
import p5 from 'p5';
import BaseSimulation from './BaseSimulation';

/**
 * Cash Flow Simulation - MedCare Regional Health System
 * 
 * Case Study Context:
 * - 12 hospitals serving 2.3M patients, $850M revenue
 * - Insurance reimbursements delayed 45-60 days
 * - Before: $75M cash buffer, $25M credit line usage, 0.5% yield
 * - After: $45M cash buffer, $8M credit line, 4.2% yield
 * - Annual savings: $2.9M
 * 
 * Visualization:
 * - Shows daily cash balance over 90-day rolling window
 * - Insurance reimbursements (blue bars) arrive with 45-60 day delay
 * - Operating expenses (red bars) are daily/weekly
 * - Green line: optimal cash buffer ($45M)
 * - Red line: minimum required ($30M)
 * - Yellow zone: credit line usage
 * - Investment opportunities shown when cash exceeds buffer
 */

const CashFlowSimulation: React.FC = () => {
  const sketch = (p: p5) => {
    // MedCare Hospital Network - Cash Flow State
    let day = 0;
    let cashBalance = 75000000; // Starting at $75M (before optimization)
    const optimalBuffer = 45000000; // $45M optimal buffer
    const minimumCash = 30000000; // $30M minimum required
    const maxCash = 100000000; // $100M scale
    
    // Historical data (90 days)
    let balanceHistory: number[] = [];
    let reimbursements: {day: number, amount: number, received: boolean}[] = [];
    let expenses: {day: number, amount: number}[] = [];
    
    let paused = false;
    let speed = 1;
    let selectedView = 'overview'; // 'overview', 'reimbursements', 'investments'
    
    p.setup = () => {
      p.createCanvas(800, 500);
      p.frameRate(30);
      
      // Initialize with starting balance
      for (let i = 0; i < 90; i++) {
        balanceHistory.push(cashBalance);
      }
      
      // Generate realistic insurance reimbursement schedule
      // Hospitals bill insurance, payment comes 45-60 days later
      for (let i = 0; i < 30; i++) {
        const billingDay = i * 3; // Bill every 3 days
        const paymentDelay = 45 + Math.floor(Math.random() * 15); // 45-60 days
        const amount = 800000 + Math.random() * 400000; // $800K-$1.2M per claim batch
        
        reimbursements.push({
          day: billingDay + paymentDelay,
          amount: amount,
          received: false
        });
      }
      
      // Generate daily operating expenses
      for (let i = 0; i < 90; i++) {
        const dailyExpense = 600000 + Math.random() * 200000; // $600K-$800K daily
        expenses.push({
          day: i,
          amount: dailyExpense
        });
      }
    };
    
    p.mousePressed = () => {
      if (p.mouseX > 0 && p.mouseX < p.width && p.mouseY > 0 && p.mouseY < p.height) {
        paused = !paused;
        return false;
      }
    };
    
    p.keyPressed = () => {
      if (p.key === ' ') paused = !paused;
      if (p.key === 'r' || p.key === 'R') {
        // Reset
        day = 0;
        cashBalance = 75000000;
        balanceHistory = [];
        for (let i = 0; i < 90; i++) {
          balanceHistory.push(cashBalance);
        }
        reimbursements.forEach(r => r.received = false);
      }
      if (p.key === '+' || p.key === '=') speed = Math.min(speed + 0.5, 3);
      if (p.key === '-' || p.key === '_') speed = Math.max(speed - 0.5, 0.5);
      if (p.key === '1') selectedView = 'overview';
      if (p.key === '2') selectedView = 'reimbursements';
      if (p.key === '3') selectedView = 'investments';
    };
    
    p.draw = () => {
      p.background(250);
      
      // Update simulation
      if (!paused && p.frameCount % Math.floor(30 / speed) === 0) {
        day++;
        if (day >= 90) day = 0; // Loop
        
        // Process daily expenses
        const todayExpense = expenses[day % expenses.length];
        cashBalance -= todayExpense.amount;
        
        // Process insurance reimbursements
        reimbursements.forEach(r => {
          if (r.day === day && !r.received) {
            cashBalance += r.amount;
            r.received = true;
          }
        });
        
        // If below minimum, use credit line (expensive at 6.2%)
        if (cashBalance < minimumCash) {
          cashBalance = minimumCash;
          // Visual indicator of credit usage
        }
        
        // Update history
        balanceHistory.push(cashBalance);
        if (balanceHistory.length > 90) balanceHistory.shift();
      }
      
      // Draw visualization
      drawCashFlowChart();
      drawMetrics();
      drawControls();
      drawLegend();
    };
    
    const drawCashFlowChart = () => {
      const chartX = 50;
      const chartY = 80;
      const chartW = 700;
      const chartH = 300;
      
      // Background
      p.fill(255);
      p.stroke(200);
      p.rect(chartX, chartY, chartW, chartH);
      
      // Draw zones
      // Investment zone (above optimal buffer)
      p.fill(200, 255, 200, 50);
      p.noStroke();
      const investY = p.map(optimalBuffer, 0, maxCash, chartY + chartH, chartY);
      p.rect(chartX, chartY, chartW, investY - chartY);
      
      // Safe zone (between optimal and minimum)
      p.fill(255, 255, 200, 50);
      const minY = p.map(minimumCash, 0, maxCash, chartY + chartH, chartY);
      p.rect(chartX, investY, chartW, minY - investY);
      
      // Credit zone (below minimum)
      p.fill(255, 200, 200, 50);
      p.rect(chartX, minY, chartW, chartY + chartH - minY);
      
      // Draw reference lines
      p.stroke(100, 200, 100);
      p.strokeWeight(2);
      p.line(chartX, investY, chartX + chartW, investY);
      p.fill(100, 200, 100);
      p.noStroke();
      p.textAlign(p.LEFT, p.CENTER);
      p.textSize(11);
      p.text('Optimal Buffer: $45M', chartX + 5, investY - 10);
      
      p.stroke(200, 100, 100);
      p.strokeWeight(2);
      p.line(chartX, minY, chartX + chartW, minY);
      p.fill(200, 100, 100);
      p.noStroke();
      p.text('Minimum Required: $30M', chartX + 5, minY + 10);
      
      // Draw cash balance line
      p.stroke(30, 100, 200);
      p.strokeWeight(3);
      p.noFill();
      p.beginShape();
      for (let i = 0; i < balanceHistory.length; i++) {
        const x = p.map(i, 0, 90, chartX, chartX + chartW);
        const y = p.map(balanceHistory[i], 0, maxCash, chartY + chartH, chartY);
        p.vertex(x, y);
      }
      p.endShape();
      
      // Draw current day marker
      const currentX = p.map(day, 0, 90, chartX, chartX + chartW);
      p.stroke(255, 100, 0);
      p.strokeWeight(2);
      p.line(currentX, chartY, currentX, chartY + chartH);
      
      // Draw upcoming reimbursements
      if (selectedView === 'reimbursements' || selectedView === 'overview') {
        reimbursements.forEach(r => {
          if (r.day >= day && r.day < day + 90 && !r.received) {
            const x = p.map(r.day - day, 0, 90, chartX, chartX + chartW);
            p.fill(100, 150, 255, 150);
            p.noStroke();
            const barH = (r.amount / maxCash) * chartH * 0.3;
            p.rect(x - 2, chartY + chartH - barH, 4, barH);
          }
        });
      }
      
      // Axes
      p.stroke(100);
      p.strokeWeight(1);
      p.line(chartX, chartY + chartH, chartX + chartW, chartY + chartH);
      p.line(chartX, chartY, chartX, chartY + chartH);
      
      // Y-axis labels
      p.fill(100);
      p.noStroke();
      p.textAlign(p.RIGHT, p.CENTER);
      p.textSize(10);
      for (let i = 0; i <= 5; i++) {
        const val = (maxCash / 5) * i;
        const y = p.map(val, 0, maxCash, chartY + chartH, chartY);
        p.text('$' + (val / 1000000).toFixed(0) + 'M', chartX - 5, y);
      }
      
      // X-axis labels
      p.textAlign(p.CENTER, p.TOP);
      for (let i = 0; i <= 90; i += 15) {
        const x = p.map(i, 0, 90, chartX, chartX + chartW);
        p.text('Day ' + i, x, chartY + chartH + 5);
      }
    };
    
    const drawMetrics = () => {
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(14);
      p.text('MedCare Regional Health System - Cash Flow Optimization', 50, 20);
      
      p.textSize(11);
      p.fill(100);
      p.text('90-Day Rolling Cash Flow Analysis | Insurance Reimbursement Delays: 45-60 days', 50, 40);
      
      // Current metrics
      const metricsY = 400;
      p.textSize(12);
      p.fill(40);
      
      const currentBalance = balanceHistory[balanceHistory.length - 1];
      const excessCash = Math.max(0, currentBalance - optimalBuffer);
      const creditUsage = Math.max(0, minimumCash - currentBalance);
      
      p.text(`Day ${day} | Cash Balance: $${(currentBalance / 1000000).toFixed(1)}M`, 50, metricsY);
      
      if (excessCash > 0) {
        p.fill(100, 180, 100);
        p.text(`💰 Investment Opportunity: $${(excessCash / 1000000).toFixed(1)}M @ 4.2% yield`, 50, metricsY + 20);
      } else if (creditUsage > 0) {
        p.fill(200, 100, 100);
        p.text(`⚠️  Credit Line Usage: $${(creditUsage / 1000000).toFixed(1)}M @ 6.2% rate`, 50, metricsY + 20);
      } else {
        p.fill(100);
        p.text(`✓ Optimal Range: Efficient cash management`, 50, metricsY + 20);
      }
      
      // Savings calculation
      const avgBalance = balanceHistory.reduce((a, b) => a + b, 0) / balanceHistory.length;
      const oldCost = (75000000 * 0.005) - (25000000 * 0.062); // Old: $75M @ 0.5%, $25M credit @ 6.2%
      const newBenefit = (avgBalance > optimalBuffer ? (avgBalance - optimalBuffer) * 0.042 : 0);
      const annualSavings = (newBenefit - oldCost) * 365 / 90;
      
      p.fill(100);
      p.text(`Projected Annual Benefit: $${(Math.abs(annualSavings) / 1000000).toFixed(2)}M`, 50, metricsY + 40);
    };
    
    const drawControls = () => {
      p.fill(100);
      p.noStroke();
      p.textAlign(p.RIGHT, p.TOP);
      p.textSize(10);
      
      const controlsX = 750;
      let y = 400;
      
      p.text('Controls:', controlsX, y);
      y += 15;
      p.text('Click: Pause/Resume', controlsX, y);
      y += 12;
      p.text('Space: Pause/Resume', controlsX, y);
      y += 12;
      p.text('R: Reset', controlsX, y);
      y += 12;
      p.text('+/-: Speed', controlsX, y);
      y += 12;
      p.text('1/2/3: Views', controlsX, y);
      
      // Status
      y += 20;
      p.fill(paused ? 200 : 100);
      p.text(paused ? 'PAUSED' : 'RUNNING', controlsX, y);
      y += 12;
      p.fill(100);
      p.text(`Speed: ${speed.toFixed(1)}x`, controlsX, y);
    };
    
    const drawLegend = () => {
      const legendX = 550;
      const legendY = 400;
      
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(10);
      p.fill(100);
      p.text('Legend:', legendX, legendY);
      
      // Blue bars
      p.fill(100, 150, 255);
      p.rect(legendX, legendY + 15, 12, 8);
      p.fill(100);
      p.text('Insurance Reimbursements', legendX + 18, legendY + 13);
      
      // Green zone
      p.fill(200, 255, 200);
      p.rect(legendX, legendY + 28, 12, 8);
      p.fill(100);
      p.text('Investment Zone', legendX + 18, legendY + 26);
      
      // Yellow zone
      p.fill(255, 255, 200);
      p.rect(legendX, legendY + 41, 12, 8);
      p.fill(100);
      p.text('Optimal Range', legendX + 18, legendY + 39);
      
      // Red zone
      p.fill(255, 200, 200);
      p.rect(legendX, legendY + 54, 12, 8);
      p.fill(100);
      p.text('Credit Line Zone', legendX + 18, legendY + 52);
    };
  };

  return (
    <BaseSimulation
      sketch={sketch}
      title="Cash Flow Optimization Simulation"
      description="Interactive visualization of MedCare Hospital's cash flow optimization using Linear Programming. Shows how the hospital manages insurance reimbursement delays, optimizes cash buffers, and minimizes credit line usage."
    />
  );
};

export default CashFlowSimulation;
